import { Component } from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { injectSwitchState, NgpSwitch, NgpSwitchThumb } from 'ng-primitives/switch';
import { ChangeFn, provideValueAccessor, TouchedFn } from 'ng-primitives/utils';

@Component({
  selector: 'joii-switch',
  hostDirectives: [
    {
      directive: NgpSwitch,
      inputs: ['ngpSwitchChecked:checked', 'ngpSwitchDisabled:disabled'],
      outputs: ['ngpSwitchCheckedChange:checkedChange'],
    },
  ],
  imports: [NgpSwitchThumb],
  template: `<span ngpSwitchThumb></span>`,
  styles: `
    @property --switch-size {
      syntax: '<length>';
      inherits: false;
      initial-value: 54px;
    }

    @property --switch-thumb-size {
      syntax: '<length>';
      inherits: false;
      initial-value: 28px;
    }

    :host {
      display: inline-flex;
      align-items: center;
      position: relative;
      width: var(--switch-size);
      border-radius: 999px;
      background-color: var(--Vetai-Backgrounds--Highlight);
      padding: 2px;
      outline: none;
      transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
      transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
      transition-duration: 150ms;
      box-sizing: border-box;
    }

    :host[data-hover] {
      background-color: var(--Vetai-Form_controls--Border_Hover);
    }

    :host[data-focus-visible] {
      outline: 2px solid var(--Vetai-Inline_links_and_info--Default);
    }

    :host[data-checked] {
      background-color: var(--Vetai-Inline_links_and_info--Default);
    }

    :host[data-checked][data-hover] {
      background-color: var(--Vetai-Inline_links_and_info--Active);
    }

    [ngpSwitchThumb] {
      display: block;
      height: var(--switch-thumb-size);
      width: var(--switch-thumb-size);
      border-radius: 999px;
      background-color: var(--Vetai-Positive_package--Default);
      box-shadow: 0 0 4px rgba(21, 33, 56, 0.2);
      outline: none;
      transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
      transform: translateX(1px);
      box-sizing: border-box;
    }

    [ngpSwitchThumb][data-checked] {
      transform: translateX(22px);
    }
  `,
  providers: [provideValueAccessor(SwitchComponent)],
  host: {
    '(focusout)': 'onTouched?.()',
  },
})
export class SwitchComponent implements ControlValueAccessor {
  private readonly switch = injectSwitchState();

  private onChange?: ChangeFn<boolean>;

  protected onTouched?: TouchedFn;

  constructor() {
    // destroy is handled by the OutputEmitterRef in the switch() method
    this.switch().checkedChange.subscribe((value) => this.onChange?.(value));
  }

  writeValue(value: boolean): void {
    // writeValue is the model -> view direction, so it must not emit: the
    // emitted change would come straight back through onChange as a view -> model
    // update and loop forever.
    this.switch().setChecked(value, { emit: false });
  }

  registerOnChange(fn: ChangeFn<boolean>): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: TouchedFn): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.switch().setDisabled(isDisabled);
  }
}
